/**
 * @fileoverview The React root's error options for the browser logger (MUTATION-INSTRUMENT-001
 * F-TOCONTAIN-3 U2b).
 *
 * React 19 routes a render error to one of three ROOT callbacks, whatever boundary is involved:
 *
 * | callback             | when                                       | React's default  | here                    |
 * | -------------------- | ------------------------------------------ | ---------------- | ----------------------- |
 * | `onUncaughtError`    | no boundary caught it                      | `reportError`    | NOT replaced — see below |
 * | `onCaughtError`      | an error boundary caught it                | `console.error`  | `logger.error`          |
 * | `onRecoverableError` | React recovered (e.g. hydration mismatch)  | `console.error`  | `logger.warn`           |
 *
 * `onUncaughtError` stays React's: its `reportError` fires `error` on `window`, which
 * `installGlobalErrorCapture` already ships — replacing it would lose that report and add nothing.
 *
 * AN APP'S HOOK (PROTECTOR-SMS-001 Unit 1): `createReactRootErrorOptions(logger, { onError })`. A root that must ACT on
 * a render error its tree never recovers from — the tenant site holding a stored document on screen until its app's
 * first ready render — passes a hook. It changes no report, only adds a call:
 *
 * | callback             | with a hook                                                                      |
 * | -------------------- | -------------------------------------------------------------------------------- |
 * | `onUncaughtError`    | INSTALLED: reported exactly as React's default (`reportError`), never the logger, then the hook |
 * | `onCaughtError`      | `logger.error` as above, then the hook (a boundary's fallback may be all that renders) |
 * | `onRecoverableError` | `logger.warn` as above; no hook (React kept rendering, so the app reaches its own ready render) |
 *
 * Without a hook the options are exactly the two above: the other roots are unchanged.
 * The other two reached no backend in nine of the ten web apps (measured 2026-09-26: only pos-web's
 * boundary reported, to a different store). Passing these options to `createRoot` / `hydrateRoot`
 * covers every boundary under the root — an app's own, page-shell's, a package's — without any
 * boundary having to know about telemetry, which is why this lives at the root and not in a
 * boundary: one mechanism, one plane (BROWSER-TELEMETRY), no per-boundary wiring to drift.
 *
 * ```js
 * import { createReactRootErrorOptions } from '@puffin-core/logging-browser';
 * import { logger } from './core/config/logger';
 * createRoot(element, createReactRootErrorOptions(logger)).render(<App />);
 * ```
 *
 * @module reactRootErrorOptions
 */

import { GLOBAL_ERROR_KIND, describeError } from './captureGlobalErrors.js';

/**
 * The boundary's name as a reader would search for it: its `displayName`, else its class name.
 * @param {Object|undefined} boundary - `errorInfo.errorBoundary`, the boundary INSTANCE
 * @returns {string|null}
 */
function boundaryName(boundary) {
  const type = boundary?.constructor;
  return type?.displayName || type?.name || null;
}

/**
 * React's own report of an uncaught render error, kept exactly (react-dom 19's `reportGlobalError`, its browser
 * branches): the browser's `reportError` where it exists; else the `error` event it would fire on `window`, printed to
 * the console only when no listener cancelled it; else, with no `ErrorEvent` at all, printed. Either way the ONE report
 * is the global capture's (`installGlobalErrorCapture`), never the logger's.
 *
 * @param {unknown} error
 * @returns {void}
 */
function reportAsReactDoes(error) {
  if (typeof globalThis.reportError === 'function') {
    globalThis.reportError(error);
    return;
  }
  if (typeof globalThis.ErrorEvent === 'function') {
    const message =
      typeof error === 'object' && error !== null && typeof error.message === 'string'
        ? error.message
        : String(error);
    const event = new globalThis.ErrorEvent('error', {
      bubbles: true,
      cancelable: true,
      message,
      error,
    });
    if (!globalThis.dispatchEvent(event)) return;
  }
  // React's own last step, kept: the browser console is where an uncancelled, uncaught render error is printed.
  console.error(error);
}

/**
 * Build the `onCaughtError` / `onRecoverableError` root options over a logger.
 *
 * @param {{error: Function, warn: Function}} logger - the configured browser logger
 * @param {{onError?: (error: unknown) => void}} [options] - `onError`: the app's hook, told of every render error
 *   its tree does not recover from (see the module table)
 * @returns {{onCaughtError: Function, onRecoverableError: Function, onUncaughtError?: Function}} the root options
 * @throws {TypeError} when the logger cannot record an error and a warning, or the hook is not a function
 */
export function createReactRootErrorOptions(logger, { onError } = {}) {
  if (!logger || typeof logger.error !== 'function' || typeof logger.warn !== 'function') {
    throw new TypeError(
      'createReactRootErrorOptions requires a logger with `error` and `warn` methods',
    );
  }
  if (onError !== undefined && typeof onError !== 'function') {
    throw new TypeError('createReactRootErrorOptions: `onError` must be a function when given');
  }

  let reporting = false;
  /**
   * Report once; a logger that throws — or that renders and re-enters — never escapes into React.
   * @param {Function} emit
   */
  function report(emit) {
    if (reporting) return;
    reporting = true;
    try {
      emit();
    } catch (_logErr) {
      // Telemetry must never become the page's second failure.
    } finally {
      reporting = false;
    }
  }

  const options = {
    onCaughtError(error, errorInfo) {
      report(() => {
        const described = describeError(error);
        const boundary = boundaryName(errorInfo?.errorBoundary);
        logger.error(
          `Render error caught by ${boundary || 'an unnamed boundary'}: ${described.name}: ${described.message}`,
          {
            kind: GLOBAL_ERROR_KIND.CAUGHT_RENDER_ERROR,
            ...described,
            componentStack: errorInfo?.componentStack ?? null,
            boundary,
          },
        );
      });
    },
    onRecoverableError(error, errorInfo) {
      report(() => {
        const described = describeError(error);
        logger.warn(
          `React recovered from a render error: ${described.name}: ${described.message}`,
          {
            kind: GLOBAL_ERROR_KIND.RECOVERABLE_RENDER_ERROR,
            ...described,
            componentStack: errorInfo?.componentStack ?? null,
          },
        );
      });
    },
  };
  if (!onError) return options;

  const caught = options.onCaughtError;
  return {
    ...options,
    onCaughtError(error, errorInfo) {
      caught(error, errorInfo);
      onError(error);
    },
    onUncaughtError(error) {
      reportAsReactDoes(error);
      onError(error);
    },
  };
}
